// Owner-approved evidence only. The client domain is deliberately withheld.
export type CaseStudy = {
  readonly id: string;
  readonly headlineGrowth: number;
  readonly growthLabel: string;
  readonly chartTitle: string;
  readonly chartLabel: string;
  readonly contentUpdatedOn: string;
  readonly title: string;
  readonly clientDescription: string;
  readonly startingPoint: string;
  readonly work: string;
  readonly results: string;
  readonly monthlyTraffic: readonly { readonly month: string; readonly year: number; readonly visits: number }[];
  readonly source: string;
  readonly testimonial?: { readonly quote: string; readonly author: string; readonly role: string; readonly company: string };
  readonly footnote: string;
};

export const mmMediaCaseStudy = {
  id: "mm-media",
  headlineGrowth: 313,
  growthLabel: "Organic traffic · June to October 2026",
  chartTitle: "Monthly organic traffic, 2026",
  chartLabel: "Ahrefs estimated monthly organic traffic",
  contentUpdatedOn: "2026-10-26",
  title: "iGaming Affiliate Site: +313% Organic Traffic in 4 Months",
  client: "MM Media",
  clientDescription: "MM Media, an iGaming affiliate site in a highly competitive niche.",
  startingPoint: "An established domain (DR 58) with flat organic traffic, between roughly 200 and 440 monthly organic visits in January to May 2026.",
  work: "From June 2026, around 15 niche-relevant guest posts per month on real iGaming and finance publishers with verified organic traffic; deep links to the money pages rather than only the homepage; a natural, varied anchor text mix.",
  results: "Organic traffic increased from 618 to 2,553 monthly visits between June and October 2026 (+313%).",
  monthlyTraffic: [
    { month: "May", year: 2026, visits: 326 },
    { month: "June", year: 2026, visits: 618 },
    { month: "July", year: 2026, visits: 701 },
    { month: "August", year: 2026, visits: 680 },
    { month: "September", year: 2026, visits: 1483 },
    { month: "October", year: 2026, visits: 2553 },
  ],
  source: "Source: Ahrefs estimated organic traffic. Client domain withheld for confidentiality.",
  testimonial: {
    quote: "Finally, a link building service that understands our industry. The team's strategic approach and transparent reporting gave us complete confidence in the process.",
    author: "Jonathan",
    role: "Head of SEO",
    company: "MM Media",
  },
  footnote: "Results vary by site, competition and content. Link building cannot guarantee rankings or traffic.",
} as const;

export const usFintechCaseStudy = {
  id: "us-fintech",
  contentUpdatedOn: "2026-10-26",
  headlineGrowth: 77,
  growthLabel: "Target-page traffic · March to August 2026",
  chartTitle: "Combined traffic to 8 target pages, 2026",
  chartLabel: "Ahrefs estimated combined monthly organic traffic to the client's 8 most-linked pages",
  title: "US Fintech Brand: +77% Organic Traffic to Target Pages in 5 Months",
  clientDescription: "A leading US fintech brand offering banking, loans and insurance.",
  startingPoint: "A high-authority domain (DR 84) competing for some of the most competitive finance keywords in the US.",
  work: "From April to August 2026, contextual guest posts on real lifestyle, business and finance publishers, linking deep to product pages (banking, savings, loans and insurance) with a natural, varied anchor text mix.",
  results: "Combined estimated organic traffic to the 8 most-linked pages grew from 125,345 to 222,070 monthly visits between March and August 2026 (+77%), and held at 221,768 in October. The banking page grew from 29,574 to 91,870 monthly visits (+211%) between March and October.",
  targetPageCount: 8,
  bankingTraffic: { march: 29574, october: 91870, growthPercent: 211 },
  monthlyTraffic: [
    { month: "March", year: 2026, visits: 125345 },
    { month: "August", year: 2026, visits: 222070 },
    { month: "October", year: 2026, visits: 221768 },
  ],
  source: "Source: Ahrefs estimated organic traffic to the client's 8 most-linked pages. Client domain withheld for confidentiality.",
  testimonial: {
    quote: "Linkzy delivered exceptional results. Our organic traffic increased by 180% in just six months, and we're now ranking on page one for our most competitive keywords.",
    author: "Kim",
    role: "Marketing Director",
    company: "US fintech brand",
  },
  footnote: "Growth happened during the campaign alongside the client's own SEO work. Results vary by site, competition and content. Link building cannot guarantee rankings or traffic.",
} as const;

export const healthcareSaasCaseStudy = {
  id: "healthcare-saas",
  title: "Healthcare SaaS: +47% Organic Traffic, Sustained for 18 Months",
  clientDescription: "A B2B healthcare software company.",
  startingPoint: "An established healthcare software brand with around 57,000 monthly organic visits in January 2025.",
  work: "A steady monthly campaign of relevant placements on healthcare, technology and business publishers, linking deep to product and resource pages with a natural, varied anchor text mix.",
  results: "Organic traffic grew from 57,495 monthly visits in January 2025 to 84,485 in June 2025 (+47%), and has stayed above 68,000 every month since, at 75,792 in October 2026 (+32% on the starting point).",
  headlineGrowth: 47,
  growthLabel: "Organic traffic · January to June 2025",
  chartTitle: "Monthly organic traffic, 2025 to 2026",
  chartLabel: "Ahrefs estimated monthly organic traffic",
  monthlyTraffic: [
    { month: "January", year: 2025, visits: 57495 },
    { month: "June", year: 2025, visits: 84485 },
    { month: "December", year: 2025, visits: 73704 },
    { month: "June", year: 2026, visits: 83014 },
    { month: "October", year: 2026, visits: 75792 },
  ],
  source: "Source: Ahrefs estimated organic traffic. Client domain withheld for confidentiality.",
  footnote: "Growth happened during the campaign alongside the client's own SEO work. Results vary by site, competition and content. Link building cannot guarantee rankings or traffic.",
  contentUpdatedOn: "2026-10-26",
} as const satisfies CaseStudy;

export const caseStudiesByRoute = {
  "/gambling-link-building": mmMediaCaseStudy,
  "/industries/fintech": usFintechCaseStudy,
  "/industries/saas": healthcareSaasCaseStudy,
} as const;

export function caseStudyFallbackHtml(study: CaseStudy = mmMediaCaseStudy) {
  const escape = (text: string) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
  return `<section data-case-study="${study.id}"><p>Case Study</p><h2>${escape(study.title)}</h2>
<p>${escape(study.clientDescription)}</p>
<h3>Starting point</h3><p>${escape(study.startingPoint)}</p>
<h3>What Linkzy did</h3><p>${escape(study.work)}</p>
<h3>Results</h3><p>${escape(study.results)}</p>
<h3>${escape(study.chartTitle)}</h3><ul>${study.monthlyTraffic.map(row => `<li>${row.month} ${row.year}: ${row.visits.toLocaleString("en-GB")} monthly organic visits</li>`).join("")}</ul>
<p>${escape(study.source)}</p>
${study.testimonial ? `<blockquote><p>${escape(study.testimonial.quote)}</p><footer>${escape(study.testimonial.author)}, ${escape(study.testimonial.role)}, ${escape(study.testimonial.company)}</footer></blockquote>` : ""}
<p>${escape(study.footnote)}</p></section>`;
}